import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Badge from '@mui/material/Badge';
import { Product } from '../data/products';

type NavKey = 'inicio' | 'productos' | 'categorias' | 'contacto';

export type CategoryNavItem = {
  label: string;
  value: string;
};

const navItems: { label: string; key: NavKey }[] = [
  { label: 'Inicio', key: 'inicio' },
  { label: 'Productos', key: 'productos' },
  { label: 'Categorías', key: 'categorias' },
  { label: 'Contacto', key: 'contacto' },
];

interface HeaderProps {
  cart?: { product: Product; quantity: number }[];
  setCartOpen?: (open: boolean) => void;
  onNavigate?: (section: NavKey) => void;
  categories?: CategoryNavItem[];
  onSelectCategory?: (category: string) => void;
}

const Header: React.FC<HeaderProps> = ({
  cart = [],
  setCartOpen,
  onNavigate,
  categories = [],
  onSelectCategory,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [categoriesAnchor, setCategoriesAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleNav = (key: NavKey) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (key === 'categorias') {
      return;
    }
    onNavigate?.(key);
  };

  const handleOpenCategoriesMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setCategoriesAnchor(event.currentTarget);
  };

  const handleCloseCategoriesMenu = () => {
    setCategoriesAnchor(null);
  };

  const handleSelectCategory = (value: string) => {
    handleCloseCategoriesMenu();
    setDrawerOpen(false);
    setMobileCategoriesOpen(false);
    onSelectCategory?.(value);
  };

  return (
    <Box component="header" sx={{ background: '#222', color: '#fff', px: 4, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        onClick={() => onNavigate?.('inicio')}
        sx={{ cursor: onNavigate ? 'pointer' : 'default' }}
      >
        <Avatar src={require('../assets/Images/LogoDefenders.jpg')} alt="Logo Defenders" sx={{ width: 52, height: 52, bgcolor: 'transparent' }} />
        <Typography variant="h5" fontWeight="bold">
          Defenders
        </Typography>
      </Stack>
      {isMobile ? (
        <>
          <IconButton color="inherit" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle}>
            <Box sx={{ width: 260, bgcolor: '#222', height: '100%', color: '#fff' }} role="presentation">
              <List>
                {navItems.map((item) => {
                  if (item.key === 'categorias') {
                    return (
                      <React.Fragment key={item.key}>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => setMobileCategoriesOpen((open) => !open)}
                            sx={{ color: '#fff' }}
                          >
                            <ListItemText primary={item.label} />
                            {mobileCategoriesOpen ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                        </ListItem>
                        <Collapse in={mobileCategoriesOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categories.map((cat) => (
                              <ListItem key={cat.value} disablePadding>
                                <ListItemButton
                                  sx={{ pl: 4, color: '#fff' }}
                                  onClick={() => handleSelectCategory(cat.value)}
                                >
                                  <ListItemText primary={cat.label} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  }

                  return (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        onClick={(e) => {
                          handleNav(item.key)(e);
                          setDrawerOpen(false);
                        }}
                        sx={{ color: '#fff' }}
                      >
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </>
      ) : (
        <nav style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          {navItems.map((item) => {
            if (item.key === 'categorias') {
              return (
                <Box key={item.key}>
                  <a
                    href="#categorias"
                    onClick={handleOpenCategoriesMenu}
                    style={{
                      color: '#fff',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {item.label}
                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                  </a>
                  <Menu
                    anchorEl={categoriesAnchor}
                    open={Boolean(categoriesAnchor)}
                    onClose={handleCloseCategoriesMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{ sx: { minWidth: 220 } }}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} onClick={() => handleSelectCategory(cat.value)}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              );
            }

            return (
              <a
                key={item.key}
                href={`#${item.key}`}
                onClick={handleNav(item.key)}
                style={{ color: '#fff', textDecoration: 'none', cursor: 'pointer' }}
              >
                {item.label}
              </a>
            );
          })}
        </nav>
      )}
      {setCartOpen && (
        <Badge badgeContent={cart.reduce((acc, item) => acc + item.quantity, 0)} color="success" sx={{ ml: 2, cursor: 'pointer' }} onClick={() => setCartOpen(true)}>
          <ShoppingCartIcon sx={{ fontSize: 32, color: '#4caf50' }} />
        </Badge>
      )}
    </Box>
  );
};

export default Header;
